import { useQuery, useMutation, useQueryClient } from '../lib/queryClient';

export interface Workflow {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  sessionId: string | null;
  tags: string[];
  n8nWorkflowId: string | null;
  n8nUrl: string | null;
  nodes: string | null;
  connections: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkflowNode {
  name: string;
  type: string;
  position: [number, number];
  parameters?: Record<string, any>;
  credentials?: Record<string, any>;
}

export interface WorkflowConnection {
  node: string;
  type?: string;
  index?: number;
}

export interface CreateWorkflowInput {
  name: string;
  description?: string;
  nodes?: WorkflowNode[] | string;
  connections?: Record<string, Record<string, WorkflowConnection[][]>> | string;
  settings?: {
    saveExecutionProgress?: boolean;
    saveManualExecutions?: boolean;
    executionTimeout?: number;
  };
  active?: boolean;
  tags?: string[];
  sessionId?: string;
}

export interface UpdateWorkflowInput {
  id: string;
  name?: string;
  description?: string;
  nodes?: string;
  connections?: string;
  active?: boolean;
  tags?: string[];
}

export interface GenerateWorkflowInput {
  prompt: string;
  sessionId?: string;
  model?: string;
}

// Fetch user's workflows
export function useWorkflows(sessionId?: string) {
  return useQuery({
    queryKey: ['workflows', sessionId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (sessionId) params.set('sessionId', sessionId);

      const response = await fetch(`/_api/n8n/workflows?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch workflows');
      }

      const data = await response.json();
      return data as { success: boolean; workflows: Workflow[]; count: number };
    },
  });
}

// Create a new workflow
export function useCreateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateWorkflowInput) => {
      const response = await fetch('/_api/n8n/workflows', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create workflow');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

// Generate workflow with AI
export function useGenerateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: GenerateWorkflowInput) => {
      const response = await fetch('/_api/n8n/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate workflow');
      }

      return response.json() as Promise<{
        success: boolean;
        workflow: {
          id: string;
          name: string;
          active: boolean;
          n8n: { id: string; url: string } | null;
        };
        generatedWorkflow: any;
      }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

// Trigger a workflow execution
export function useTriggerWorkflow() {
  return useMutation({
    mutationFn: async ({
      workflowId,
      payload,
    }: {
      workflowId: string;
      payload?: Record<string, any>;
    }) => {
      const response = await fetch('/_api/n8n/trigger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ workflowId, payload }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to trigger workflow');
      }

      return response.json();
    },
  });
}

// Update a workflow
export function useUpdateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateWorkflowInput) => {
      const { id, ...data } = input;
      const response = await fetch(`/_api/n8n/workflows/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update workflow');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}

// Delete a workflow
export function useDeleteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workflowId: string) => {
      const response = await fetch(`/_api/n8n/workflows/${workflowId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete workflow');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}
